<script setup lang="ts">
/**
 * Aura AGM (CAU002) — Studio panel. Live document package + create task.
 * 자체완결 cdd(useCddFill) + normalizeAgmForWord. E-Filing 없음. 서명은 이름 기반 auto-signer.
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, scopeOf, typeOf } from '../cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '../cdd/agmSignersContext';
import AgmPackageDoc from '../AgmPackageDoc.vue';
import { normalizeAgmForWord } from '../utils/normalizeAgmForWord';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  // 스튜디오 주소 입력(📍 → 챗봇) 결과. key 로 대상 필드를 지정해 되돌려준다(인코퍼레이션과 동일 경로).
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const docRef = ref<InstanceType<typeof AgmPackageDoc> | null>(null);
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
// Edit 재진입: 저장값 복원 후엔 회사 자동채움(비동기, 복원보다 늦게 끝남)이 직접 고친 값·부수(DDF/Notice)를 덮지 않게 한다.
//   빈 칸만 채우고 부수는 유지. 다른 회사로 바꾼 경우에만 새 회사 값으로 다시 채운다.
const restoredFromSaved = ref(false);
// 자동채움 결과(DDF/Notice 부수)를 반응형으로 패키지에 전달 — 마운트 타이밍 무관.
const autoLocal = ref<{ ddfIds: number[]; noticeIds: number[] }>({ ddfIds: [1], noticeIds: [] });
// 업무 설정의 processSteps (doc / t-sign / e-filing) — taskFlow 파생용.
const taskConfigSteps = ref<string[]>([]);

function buildAddress(a: any): string {
  if (!a) return '';
  if (typeof a === 'string') return a.trim();
  const line = [a.address1, a.address2, [a.postalCode, a.city].filter(Boolean).join(' '), a.country]
    .map((s: any) => String(s || '').trim()).filter(Boolean).join(', ');
  return line;
}
/** 주소 파트 → 한 줄. camelCase / UPPER_CASE 필드명 모두 지원. 해외주소 우선, 없으면 SG 주소. */
function buildCtrlAddress(dt: any): string {
  if (!dt || typeof dt !== 'object') return '';
  const g = (...keys: string[]) => { for (const k of keys) { const v = String((dt as any)[k] ?? '').trim(); if (v) return v; } return ''; };
  const fl1 = g('foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1');
  const fl2 = g('foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2');
  if (fl1 || fl2) return [fl1, fl2].filter(Boolean).join(', ');
  const block = g('blockNo', 'BLOCK_NO', 'block');
  const street = g('streetName', 'STREET_NAME', 'street');
  const building = g('buildingName', 'BUILDING_NAME', 'building');
  const level = g('addrLevel', 'ADDR_LEVEL', 'level');
  const unitNo = g('addrUnit', 'ADDR_UNIT', 'unit');
  const postal = g('postalCode', 'POSTAL_CODE');
  const unit = [level, unitNo].filter(Boolean).join('-');
  return [
    [block, street].filter(Boolean).join(' '),
    building,
    unit ? `#${unit}` : '',
    postal ? `Singapore ${postal}` : '',
  ].filter(Boolean).join(', ');
}
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return {
    id: `agm-sg-${signerSeq++}`,
    name: String(name || '').trim(),
    title,
    role,
    isManual: true,
    showTitle: true,
    _signerLayout: 'box',
  };
}

function isoDate(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : s;
}

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
// SignersField 의 직책별 서명자 선택 다이얼로그(Director/Secretary/Shareholder/Controller/Contact)를 켜기 위해 하위에 제공.
provide(AGM_ENTITY_CD, entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const pct = Math.min(100, Math.round((fill.filledCount.value / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

// 필드 ✨/📍 활성 → 챗봇 조회 요청 (activeKey 는 엔진이 추적)
/** person 필드 → ✨ 기본 조회 역할을 필드 맥락으로 결정.
 *   Notice for Controller = 그 엔티티의 CONTROLLER, 디렉터 칸 = DIRECTOR, 멤버 칸 = SHAREHOLDER.
 *   나머지는 client-wide CONTACT. 역할 조회는 해당 엔티티 스코프(entityCd)로 리스트를 띄운다. */
function lookupRoleFor(key: string): { role: string; scoped: boolean } {
  const b = bareKey(key);
  const sc = scopeOf(key);
  // secretary_name 은 회사 비서(공유키) — Notice 의 ctrl_ 스코프 안에 있어도 SECRETARY 로 (스코프 체크보다 먼저).
  if (b === 'secretary_name') return { role: 'SECRETARY', scoped: true };
  if (/^(individual_)?controller_/.test(b) || /^ctrl_/.test(sc)) return { role: 'CONTROLLER', scoped: true };
  if (b === 'director_name' || b === 'registered_holder' || /^ddf_/.test(sc)) return { role: 'DIRECTOR', scoped: true };
  if (b === 'member_name' || /_member_/.test(b)) return { role: 'SHAREHOLDER', scoped: true };
  return { role: 'CONTACT', scoped: false };
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    const { role, scoped } = lookupRoleFor(key);
    emit('request-person-lookup', {
      fieldGroup: 'aura-agm',
      key: bareKey(key),
      scopedKey: key,
      defaultRole: role,
      lookupType: 'officer',
      entityCd: scoped ? entityCd.value : '',
    });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

// ── 자동채움: 회사 선택 → 회사·주소·FYE·이사명단 반영 (entityMyApi) ──────
//   인코퍼레이션 패널의 autofill 과 동일 접근 — 엔티티 상세 + director officer 조회.
async function autofillFromEntity(entityCdRaw: string) {
  const cd = String(entityCdRaw || '').trim();
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  if (entityChanged) restoredFromSaved.value = false;
  console.warn('[AGM] autofill entityCd used =', cd, '(controller DB row 의 ENTITY_CD 와 같아야 컨트롤러가 뜹니다)');
  for (const k of autofilledKeys.value) fill.setValue(k, ''); // 이전 자동값 정리
  // 엔티티 Change 시: 이전 엔티티의 서명자(디렉터/멤버/컨트롤러)와 인물 스코프 값 전부 초기화 →
  //   재시드 가드(if !signersOf.length)에 막히지 않고 새 엔티티로 다시 채운다.
  if (entityChanged) {
    signersCtx.clearAll();
    for (const k of Object.keys(fill.values)) {
      if (/^(ddf_\d+\/|ctrl_\d+\/)/.test(k)) fill.setValue(k, '');
    }
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = String(v ?? '').trim();
    if (restoredFromSaved.value && String(fill.values[k] ?? '').trim()) return; // 저장값 보존
    if (s) { fill.setValue(k, s); written.add(k); }
  };

  let ddfCount = 1;
  let noticeIds: number[] = [];

  // 1) L_ENTITY 원본 — 회사명 / 등록번호 / 등록사무소 주소 / FYE / 회사유형코드 / 주주(멤버)
  //   ⚠ getEntityById/profile 은 가공값(회사타입명 'Local Entity', 사업활동 객체)을 돌려줘 못 씀.
  //     전용 엔드포인트 `/api/entity/:id/agm-source` 가 L_ENTITY 테이블을 직접 읽어 원본을 준다.
  let agmSrcMembers: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' })
      .then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
    set('financial_year_end', isoDate(src.financialYearEnd));
    agmSrcMembers = Array.isArray(src.members) ? src.members : [];
    console.warn('[AGM] agm-source → addr:', src.registeredOfficeAddress || '(empty)',
      '| ctCode:', src.companyTypeCode || '(none)', '| members:', agmSrcMembers.length);
    // 회사유형 자동체크 (Letter of AR) — L_ENTITY.COMPANY_TYPE 원본코드(A04) 매핑.
    //   A04001 Private Company - Exempt → EPC / A04002 Non-Exempt → Private / A04003 Public(Shares) / A04004 Public(Guarantee)
    const ctCode = String(src.companyTypeCode || '').trim().toUpperCase();
    const A04_MAP: Record<string, string> = {
      A04001: 'epc_shares', A04002: 'private_shares', A04003: 'public_shares', A04004: 'public_guarantee',
    };
    if (A04_MAP[ctCode]) set('company_type', A04_MAP[ctCode]);
    console.warn('[AGM] company_type: code', ctCode, '→', fill.values.company_type || '(unresolved)');
    // 상태 기본값(대부분 케이스) — 운영자가 필요 시 변경
    set('company_activity', 'active');
    set('company_solvency', 'solvent');
  } catch (e) { console.warn('[AGM] agm-source failed', e); }

  // 3) Directors — officer(director) → 이사명(= 서명자). 이름 리스트와 싸이너를 하나로 통합:
  //    directors 는 서명자(agm_Signers)로만 관리하고, DDF 는 이사 1인당 1부 + 각자 서명자 시드.
  const directorNames: string[] = [];
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'director' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    const officers: any[] = [...(off.autoOfficers || []), ...(off.manualOfficers || [])]
      .filter((o) => String(o?.status || '') !== 'Cessation');
    for (const o of officers) {
      const nm = String(o.fullName || o.name || '').trim();
      if (nm && !directorNames.includes(nm)) directorNames.push(nm);
    }
    console.debug('[AGM] directors found:', directorNames.length, directorNames);
  } catch (e) { console.warn('[AGM] getEntityOfficer failed', e); }
  if (directorNames.length) {
    ddfCount = directorNames.length;
    set('agm_director_count', String(directorNames.length));
    set('director_name', directorNames[0]);
    // 이사 서명자 시드 (하나로 묶인 signers) — AGM Documents(agm_Signers) + Letter(letter_Signers)
    const dirSigners = directorNames.map((nm) => mkSigner(nm, 'Director'));
    if (!signersCtx.signersOf('agm_Signers').length) signersCtx.setSigners('agm_Signers', [...dirSigners]);
    if (!signersCtx.signersOf('letter_Signers').length) signersCtx.setSigners('letter_Signers', [...dirSigners]);
    // DDF 는 이사별 1부 — 각자 그 이사만 서명자로 + 이해관계(Disclosure) 자동채움
    const norm = (s: string) => String(s || '').toUpperCase().replace(/\s+/g, ' ').trim();
    const fmtShares = (m: any): string => {
      const parts: string[] = [];
      if (Number(m?.sharesOrdinary) > 0) parts.push(`${Number(m.sharesOrdinary).toLocaleString()} Ordinary Shares`);
      if (Number(m?.sharesPreference) > 0) parts.push(`${Number(m.sharesPreference).toLocaleString()} Preference Shares`);
      if (Number(m?.sharesOthers) > 0) parts.push(`${Number(m.sharesOthers).toLocaleString()} Other Shares`);
      return parts.join(', ');
    };
    directorNames.forEach((nm, i) => {
      const sc = `ddf_${i + 1}/`;
      const sk = `${sc}ddf_Signers`;
      if (!signersCtx.signersOf(sk).length) signersCtx.setSigners(sk, [mkSigner(nm, 'Director')]);
      set(`${sc}director_name`, nm);
      // ⚠ 어느 이해관계 섹션이 해당되는지는 알 수 없다(가족/deemed/법인체/타법인).
      //   유일하게 아는 것 = 이 이사가 **이 회사 주주**이기도 한 경우의 own-name 지분.
      //   그 경우에만 own_name 섹션을 체크하고 주식수를 채운다. 나머지는 건드리지 않음(운영자 입력).
      const mem = agmSrcMembers.find((m) => m?.name && norm(m.name) === norm(nm));
      if (mem && Number(mem.sharesTotal) > 0) {
        set(`${sc}own_name_interests`, 'yes');
        set(`${sc}own_name_registered_holder`, nm);
        set(`${sc}own_name_interest_description`, fmtShares(mem));
        set(`${sc}own_name_amount_at_appointment`, Number(mem.sharesTotal).toLocaleString());
        set(`${sc}own_name_amount_at_end`, Number(mem.sharesTotal).toLocaleString());
      }
    });
  }

  // 4) Sole Member — L_SHAREHOLDER_DETAIL/L_SHARE_CAPITAL_DETAIL 의 실제 주주(멤버).
  //    ⚠ 솔멤버는 **주주**여야 하며 디렉터가 오면 안 된다 → agm-source.members 만 사용.
  //    개인(B09002) 우선, 없으면 첫 멤버. 멤버 particulars 도 함께 채움.
  try {
    const indiv = agmSrcMembers.find((m) => m?.isIndividual && m?.name);
    const member = indiv || agmSrcMembers.find((m) => m?.name) || null;
    const memberName = String(member?.name || '').trim();
    console.warn('[AGM] sole member →', memberName || '(empty)', '| src members:', agmSrcMembers.length);
    if (memberName) {
      set('member_name', memberName);
      set('member_registration_no', member?.registrationNo);
      set('member_identification_no', member?.registrationNo);
      set('member_nationality', member?.nationalityCd);
      set('member_email', member?.email);
      set('member_contact_no', member?.contactNo);
      if (!signersCtx.signersOf('agm_member_Signers').length) {
        signersCtx.setSigners('agm_member_Signers', [mkSigner(memberName, 'Sole Member', 'shareholder')]);
      }
    }
  } catch (e) { console.warn('[AGM] sole member failed', e); }

  // 4b) Secretary — Notice 발송자(Company Secretary) 자동채움 (top-level secretary_name)
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'secretary' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    const secs: any[] = [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => String(o?.status || '') !== 'Cessation');
    const secName = String(secs[0]?.fullName || secs[0]?.name || '').trim();
    if (secName) set('secretary_name', secName);
  } catch (e) { console.warn('[AGM] secretary failed', e); }

  // 5) Controllers — 컨트롤러 명수만큼 Notice for Controller **문서**를 추가 + 이름·서명자 시드
  //   ⚠ 컨트롤러 실데이터는 L_CONTROLLERMANAGE(=`/api/entity/controller/:id`) 에 있다.
  //     entity_my 쪽(L_BENEFICIALOWNER)이 아니라 이 엔드포인트를 써야 조회된다.
  try {
    const res: any = await fetch(`/api/entity/controller/${encodeURIComponent(cd)}`, { credentials: 'include' })
      .then((r) => r.json());
    const cands = [res?.data, res?.data?.data, res];
    let rawCtrls: any[] = [];
    for (const c of cands) {
      if (Array.isArray(c?.controllers)) { rawCtrls = c.controllers; break; }
      if (Array.isArray(c)) { rawCtrls = c; break; }
    }
    console.warn('[AGM] CTRL (L_CONTROLLERMANAGE) →', rawCtrls.length, 'rows');
    const seen = new Set<string>();
    const active: any[] = [];
    for (const c of rawCtrls) {
      if (/\d/.test(String(c?.dateOfCessation || ''))) continue; // 실제 종료일 있으면 제외
      const nm = String(c?.name || c?.fullName || c?.FULL_NAME || '').trim();
      if (!nm || seen.has(nm.toLowerCase())) continue;
      seen.add(nm.toLowerCase()); active.push({ ...c, _name: nm });
    }
    for (let i = 0; i < active.length; i++) {
      const c = active[i];
      const sc = `ctrl_${i + 1}/`;
      const nm = c._name;
      set(sc + 'individual_controller_name', nm);
      set(sc + 'controller_full_name', nm);
      const sk = `${sc}notice_Signers`;
      if (!signersCtx.signersOf(sk).length) signersCtx.setSigners(sk, [mkSigner(nm, 'Controller', 'contact')]);
      // 컨트롤러 상세 particulars 채움 (별칭·주소·이메일·연락처·국적·ID·DOB·becoming)
      try {
        const ctrlIdx = c.ctrlIdx ?? c.CTRL_IDX;
        const rt = c.registerType || c.CTRL_REGISTER_TYPE || 'A06001';
        if (ctrlIdx != null && String(ctrlIdx) !== '') {
          const dres: any = await fetch(
            `/api/entity/controller/${encodeURIComponent(cd)}/detail?ctrlIdx=${encodeURIComponent(String(ctrlIdx))}&registerType=${encodeURIComponent(String(rt))}`,
            { credentials: 'include' },
          ).then((r) => r.json());
          const dt: any = dres?.data ?? dres ?? {};
          set(sc + 'controller_alias', dt.aliases);
          set(sc + 'controller_email', dt.contactEmail);
          set(sc + 'controller_contact_no', [dt.areaCode, dt.mobileNo].map((s: any) => String(s || '').trim()).filter(Boolean).join(' '));
          set(sc + 'controller_nationality', dt.nationalityName);
          set(sc + 'controller_identification_no', dt.identificationNo);
          set(sc + 'individual_controller_identification_no', dt.identificationNo);
          set(sc + 'controller_date_of_birth', dt.dateOfBirth);
          set(sc + 'controller_date_of_becoming', dt.dateOfAppointed);
          set(sc + 'controller_date_of_cessation', dt.dateOfCessation);
          set(sc + 'controller_residential_address', buildCtrlAddress(dt));
        }
      } catch (e) { console.warn('[AGM] ctrl detail failed', e); }
    }
    noticeIds = active.map((_, i) => i + 1);
    console.warn('[AGM] controllers → notice docs:', active.length, active.map((c) => c._name));
  } catch (e) { console.error('[AGM] getEntityController FAILED', e); }

  // 부수(DDF/Notice) 반영 — 반응형 prop 으로 전달(마운트 타이밍 무관).
  if (!restoredFromSaved.value) autoLocal.value = {
    ddfIds: Array.from({ length: Math.max(1, ddfCount) }, (_, i) => i + 1),
    noticeIds,
  };
  console.debug('[AGM] autoLocal set → ddf:', autoLocal.value.ddfIds.length, 'notices:', autoLocal.value.noticeIds.length);

  autofilledKeys.value = written;
  emitProgress();
}

// 회사 선택(chatFormData / entityData) → 자동채움
watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

/** person 객체에서 값 안전 추출 (camelCase / UPPER_CASE / 별칭 모두). */
function personField(p: any, ...keys: string[]): string {
  for (const k of keys) { const v = String(p?.[k] ?? '').trim(); if (v) return v; }
  return '';
}
/** 컨트롤러 스코프 필드(ctrl_N/…)에 선택 인물의 particulars 를 문서 키에 맞춰 채운다. */
function applyControllerPerson(scope: string, p: any) {
  const set = (k: string, v: string) => { if (String(v ?? '').trim()) fill.setValue(scope + k, String(v).trim()); };
  const name = personField(p, 'name', 'fullName', 'contactName', 'CONTACT_NAME');
  set('controller_full_name', name);
  set('individual_controller_name', name);
  set('controller_alias', personField(p, 'aliases', 'alias', 'aka'));
  set('controller_residential_address', buildCtrlAddress(p) || personField(p, 'residentialAddress', 'address', 'primaryAddress'));
  set('controller_email', personField(p, 'email', 'contactEmail', 'EMAIL'));
  set('controller_contact_no', personField(p, 'contactNo', 'mobileNo', 'MOBILE_NO')
    || [personField(p, 'areaCode'), personField(p, 'mobileNo')].filter(Boolean).join(' '));
  set('controller_nationality', personField(p, 'nationalityName', 'nationality', 'NATIONALITY_NAME'));
  const idNo = personField(p, 'identificationNo', 'nricNo', 'idNo', 'contactRegNo', 'IDENTIFICATION_NO');
  set('controller_identification_no', idNo);
  set('individual_controller_identification_no', idNo);
  set('controller_date_of_birth', personField(p, 'dateOfBirth', 'dob'));
  set('controller_date_of_becoming', personField(p, 'dateOfAppointed', 'dateOfBecoming'));
}

// 선택한 인물 → 활성 키에 적용
watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  const key = fill.activeKey.value;
  const bare = bareKey(key);
  // 비서는 Notice 스코프 안에 있어도 컨트롤러로 채우면 안 됨 → 일반 적용(이름).
  if (bare === 'secretary_name') {
    fill.applyPersonToActive(p as Record<string, any>);
  } else if (/^(individual_)?controller_/.test(bare)) {
    // 컨트롤러 칸이면 particulars 전체를 문서 키(controller_*)에 맞춰 채운다(파생 stem 불일치 회피).
    applyControllerPerson(scopeOf(key), p as Record<string, any>);
  } else {
    fill.applyPersonToActive(p as Record<string, any>);
  }
  emitProgress();
});
// 선택한 주소 → 활성 키에 적용 (레거시 selectedAddress 경로)
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
// 스튜디오 주소 입력(📍 → 챗봇) 결과 → a.key 필드에 한 줄 조립해 적용.
//   ⚠ 스튜디오 경로는 studioAppliedAddress(=appliedAddress, key 포함)로 돌아온다. selectedAddress 가 아님.
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}
function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) docRef.value?.restoreLocalState?.(s.local);
}

const TASK_CODE = 'CAU002';

// 이름 → auto-signer
function autoSigners(names: Array<{ name: unknown; title: string }>): any[] | undefined {
  const out: any[] = [];
  names.forEach((n, i) => {
    const nm = String(n.name ?? '').trim();
    if (!nm) return;
    out.push({ id: `agm-${i}`, name: nm, title: n.title, role: 'director', isManual: true, showTitle: true, _signerLayout: 'box' });
  });
  return out.length ? out : undefined;
}

type AgmKind = 'letter' | 'agm' | 'ddf' | 'notice' | 'other';
function classifyAgmDoc(name: string): AgmKind {
  const n = String(name || '').toLowerCase().replace(/[_\-.]+/g, ' ');
  if (/\bddf\b|disclosure of interest|declaration form/.test(n)) return 'ddf';
  if (/notice.*controller|change in particulars|386ah/.test(n)) return 'notice';
  if (/letter|notification|lodgement|annual return/.test(n)) return 'letter';
  if (/agm|resolutions in writing|annual general meeting|minutes|statement|solvency/.test(n)) return 'agm';
  return 'other';
}

const DDF_OWNED = [
  // 4개 이해관계 구분별 개별 키(own_name_* / family_* / deemed_* / corporate_*).
  ...['own_name', 'family', 'deemed', 'corporate'].flatMap((p) =>
    ['registered_holder', 'interest_description', 'amount_at_appointment', 'amount_at_end', 'date_price_details'].map((f) => `${p}_${f}`)),
  'corporation_name', 'interest_nature', 'director_name', 'resolution_date',
  'own_name_interests', 'family_interests', 'deemed_interests', 'corporate_interests', 'other_interests',
];
const NOTICE_OWNED = [
  'notice_date', 'individual_controller_name', 'individual_controller_identification_no',
  'is_still_controller', 'date_ceased_as_controller', 'has_change', 'date_of_change', 'particulars_of_change',
  'dated_on', 'controller_full_name', 'controller_alias', 'controller_residential_address', 'controller_email',
  'controller_contact_no', 'controller_nationality', 'controller_identification_no', 'controller_date_of_birth',
  'controller_date_of_becoming', 'controller_date_of_cessation',
];

function scopedFormData(scopePrefix: string, owned: string[]): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) {
    if (k.includes('/')) continue;
    if (owned.includes(bareKey(k))) continue; // 최상위 값 차단 — 스코프 값만
    out[k] = v;
  }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scopePrefix)) continue;
    const bk = bareKey(k);
    // ⚠ 빈 스코프 값이 채워진 최상위(공유) 값을 덮어 지우는 것 방지.
    //   company_name/registration_no/address 등 공유키는 각 문서에도 스코프 키가 등록되는데,
    //   그게 비어 있으면(엔티티 Change 리셋 등) 최상위 회사명을 공란으로 클로버 → 문서 누락.
    const sv = String(v ?? '').trim();
    if (sv === '' && bk in out && String(out[bk] ?? '').trim() !== '') continue;
    out[bk] = v;
  }
  return normalizeAgmForWord(out);
}
function companyFormData(): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) { if (!k.includes('/')) out[k] = v; }
  return normalizeAgmForWord(out);
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    const companyBase = companyFormData();
    const local: any = docRef.value?.gatherLocalState?.() || { ddfIds: [1], noticeIds: [] };

    // 이사 = agm_Signers 서명자(하나로 묶인 목록)에서 director 역할만.
    const agmSigners = signersCtx.signersOf('agm_Signers');
    const directorSigners = agmSigners.filter((s) => (s.role || '') === 'director' || /director/i.test(s.title || ''));
    // DIRECTORS/SIGNATURES 반복부용 directors 배열을 companyBase 에 주입.
    companyBase.directors = directorSigners.map((s) => ({ director_name: s.name }));
    const dirNames: Array<{ name: unknown; title: string }> =
      directorSigners.map((s) => ({ name: s.name, title: 'Director' }));
    const memberName = String(fill.values.member_name || '').trim();
    // Minutes 의 "Sole Member" 서명 자리 — docx 는 전역 [[Signers]](=이사)가 아니라 별도 변수 [[sole_member_name]] 을
    //   써야 솔 멤버가 찍힌다(비서 [[secretary_name]] 와 동일 패턴). 화면 Sole Member 싸이너 우선, 없으면 member_name.
    companyBase.sole_member_name = String(signersCtx.signersOf('agm_member_Signers')[0]?.name || memberName || '').trim();

    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SG_AGM_PRE',
      isMyPreset: props.selection?.isMyPreset === true,
      formData: companyBase,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'AGM (Unaudited, Sole Member)',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => {
            const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
            return {
              createDocument: steps.length === 0 ? true : steps.includes('doc'),
              requestTSign: steps.includes('t-sign'),
              proceedWithEFiling: steps.includes('e-filing'),
            };
          })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing documents…';
    // Edit 재진입 시 selection.documents 가 원본 템플릿이 아니라 **생성된 부수 문서**(같은 tmpId 다수,
    //   이름에 "— Director 1" 등 접미사)일 수 있음 → tmpId 기준 dedupe 해야 인당 확장이 중복되지 않는다.
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid);
      return true;
    });
    const companyLabel = String(companyBase.company_name || 'Company');
    // 생성 파일명에 이미 붙은 인당 접미사(pushOnce 가 " — <suffix>" em dash 로 추가)를 제거해
    //   재저장 시 접미사가 누적되지 않게 한다. 원본 템플릿명은 " - "(hyphen)만 쓰므로 안전.
    const stripSuffix = (s: string) => s.split(' — ')[0].trim();
    type DocSave = { templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: any[] };
    const docsToSave: DocSave[] = [];
    function pushOnce(d: any, formData: Record<string, any>, person: string, suffix = '', signers?: any[]) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = stripSuffix(String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, ''));
      docsToSave.push({
        templateId: tid,
        label: suffix ? `${baseLabel} — ${suffix}` : baseLabel,
        personName: person,
        formData: normalizeAgmForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    const pick = (key: string, fallback?: any[]) => {
      const s = signersCtx.signersOf(key);
      return s.length ? s : (fallback && fallback.length ? fallback : undefined);
    };
    for (const d of docs) {
      const kind = classifyAgmDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'ddf') {
        const ids: number[] = local.ddfIds?.length ? local.ddfIds : [1];
        for (const id of ids) {
          const fd = scopedFormData(`ddf_${id}/`, DDF_OWNED);
          const person = String(fd.director_name || `Director ${id}`);
          const signers = pick(`ddf_${id}/ddf_Signers`, autoSigners([{ name: fd.director_name, title: 'Director' }]));
          pushOnce(d, fd, person, dirNames.length > 1 ? `Director ${id}` : '', signers);
        }
        continue;
      }
      if (kind === 'notice') {
        const ids: number[] = local.noticeIds?.length ? local.noticeIds : [];
        for (const id of ids) {
          const fd = scopedFormData(`ctrl_${id}/`, NOTICE_OWNED);
          const person = String(fd.individual_controller_name || `Controller ${id}`);
          const signers = pick(`ctrl_${id}/notice_Signers`, autoSigners([{ name: fd.individual_controller_name, title: 'Controller' }]));
          pushOnce(d, fd, person, ids.length > 1 ? `Controller ${id}` : '', signers);
        }
        continue;
      }
      // letter / agm — 회사 단위 문서. 화면 지정 서명자 우선, 없으면 이사(+회원) 자동.
      const autoCompany = autoSigners([
        ...dirNames,
        ...(memberName ? [{ name: memberName, title: 'Sole Member' }] : []),
      ]);
      const signers = kind === 'letter'
        ? pick('letter_Signers', autoCompany)
        : pick('agm_Signers', autoCompany);
      pushOnce(d, companyBase, companyLabel, '', signers);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) { progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`; }
      } catch (err: any) {
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });

// scopeOf 는 향후 스코프 유틸용 — 미사용 경고 방지 위해 참조.
void scopeOf;
</script>

<template>
  <div class="agm-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'AGM (Unaudited, Sole Member)' }}</div>
        <div class="co-subtitle">Aura AGM — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <AgmPackageDoc ref="docRef" :auto-local="autoLocal" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.agm-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.agm-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
